import { useState, useEffect, useReducer, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  deleteDoc, 
  onSnapshot
} from "firebase/firestore";
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  onAuthStateChanged 
} from "firebase/auth";

// ─── Firebase Configuration ────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAmdvdeX6fzLNydEZQ9MBcWoXjRlhFbzs0",
  authDomain: "job-s-crm.firebaseapp.com",
  projectId: "job-s-crm",
  storageBucket: "job-s-crm.firebasestorage.app",
  messagingSenderId: "427867445967",
  appId: "1:427867445967:web:6c159785c02b8d82f835ba"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();

// ─── Data helpers ──────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 10);
const now = () => new Date().toISOString();
const fmt = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const TAGS = ["Lead", "Active Client", "Past Client", "Referral", "Prospect"];
const TAG_COLORS = {
  Lead: { bg: "#FFF5E6", text: "#C05621", border: "#FBBF24" },
  "Active Client": { bg: "#E6F6ED", text: "#22543D", border: "#68D391" },
  "Past Client": { bg: "#F3E8FF", text: "#553C9A", border: "#B794F4" },
  Referral: { bg: "#E6F2FF", text: "#1E4E8C", border: "#63B3ED" },
  Prospect: { bg: "#FEF5E7", text: "#975A16", border: "#F6AD55" },
};

const CITIES = ["NYC", "Chicago", "Boston", "Dallas", "Houston", "Other"];

// NEW: Helper function to get icon for activity type
const getActivityIcon = (type) => {
  switch (type) {
    case "meal": return "🍽️";
    case "call": return "📞";
    case "text": return "💬";
    case "email": return "✉️";
    case "meeting": return "🤝";
    case "note": return "📝";
    case "system": return "⚙️";
    default: return "📋";
  }
};

// ─── Firebase Helper Functions ──────────────────────────────
const saveContact = async (userId, contact) => {
  await setDoc(doc(db, `users/${userId}/contacts`, contact.id), contact);
};

const deleteContact = async (userId, contactId) => {
  await deleteDoc(doc(db, `users/${userId}/contacts`, contactId));
};

const saveActivity = async (userId, activity) => {
  await setDoc(doc(db, `users/${userId}/activities`, activity.id), activity);
};

const deleteActivity = async (userId, activityId) => {
  await deleteDoc(doc(db, `users/${userId}/activities`, activityId));
};

const saveTask = async (userId, task) => {
  await setDoc(doc(db, `users/${userId}/tasks`, task.id), task);
};

const deleteTask = async (userId, taskId) => {
  await deleteDoc(doc(db, `users/${userId}/tasks`, taskId));
};

// ─── Reducer ──────────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {
    case "SET_CONTACTS":
      return { ...state, contacts: action.payload };
    case "SET_ACTIVITIES":
      return { ...state, activities: action.payload };
    case "SET_TASKS":
      return { ...state, tasks: action.payload };
    default:
      return state;
  }
}

// ─── Icons ──────────────────────────────────────────────
const Icons = {
  dashboard: (props) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><rect x={3} y={3} width={7} height={7} /><rect x={14} y={3} width={7} height={7} /><rect x={14} y={14} width={7} height={7} /><rect x={3} y={14} width={7} height={7} /></svg>,
  users: (props) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx={9} cy={7} r={4} /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  activity: (props) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
  tasks: (props) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
  table: (props) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><rect x={3} y={3} width={18} height={18} rx={2} ry={2} /><line x1={3} y1={9} x2={21} y2={9} /><line x1={9} y1={21} x2={9} y2={9} /></svg>,
  plus: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><line x1={12} y1={5} x2={12} y2={19} /><line x1={5} y1={12} x2={19} y2={12} /></svg>,
  phone: (props) => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
  mail: (props) => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
  trash: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  edit: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  x: (props) => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><line x1={18} y1={6} x2={6} y2={18} /><line x1={6} y1={6} x2={18} y2={18} /></svg>,
  search: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx={11} cy={11} r={8} /><path d="m21 21-4.35-4.35" /></svg>,
  check: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="20 6 9 17 4 12" /></svg>,
  chevronLeft: (props) => <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="15 18 9 12 15 6" /></svg>,
  calendar: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><rect x={3} y={4} width={18} height={18} rx={2} ry={2} /><line x1={16} y1={2} x2={16} y2={6} /><line x1={8} y1={2} x2={8} y2={6} /><line x1={3} y1={10} x2={21} y2={10} /></svg>,
  building: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><rect x={4} y={2} width={16} height={20} rx={2} ry={2} /><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" /></svg>,
  bell: (props) => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>,
  clock: (props) => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx={12} cy={12} r={10} /><polyline points="12 6 12 12 16 14" /></svg>,
  signOut: (props) => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1={21} y1={12} x2={9} y2={12} /></svg>,
};

// ─── Theme ──────────────────────────────────────────────
const theme = {
  bg: "#F0F4F8",
  surface: "#FFFFFF",
  surfaceHover: "#F7FAFC",
  border: "#D1E0EB",
  borderLight: "#E2EAF0",
  text: "#1A365D",
  textMuted: "#4A5568",
  textDim: "#718096",
  accent: "#3182CE",
  accentHover: "#2C5AA0",
  accentLight: "#EBF4FF",
  success: "#38A169",
  danger: "#E53E3E",
  warning: "#DD6B20",
};

const font = `-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
const mono = `"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace`;

// ─── Button Component ──────────────────────────────────────
const Button = ({ children, variant = "default", size = "md", ...props }) => {
  const baseStyles = {
    fontFamily: font, fontWeight: 600, border: "none", borderRadius: 10, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center",
    transition: "all 0.15s", outline: "none",
  };

  const sizeStyles = {
    sm: { fontSize: 12, padding: "6px 12px" },
    md: { fontSize: 13, padding: "10px 18px" },
  };

  const variantStyles = {
    default: { background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` },
    primary: { background: theme.accent, color: "#fff" },
    danger: { background: theme.danger, color: "#fff" },
    ghost: { background: "transparent", color: theme.textMuted },
  };

  return (
    <button
      style={{ ...baseStyles, ...sizeStyles[size], ...variantStyles[variant] }}
      onMouseEnter={(e) => {
        if (variant === "primary") e.currentTarget.style.background = theme.accentHover;
        else if (variant === "danger") e.currentTarget.style.background = "#C53030";
        else if (variant === "ghost") e.currentTarget.style.background = theme.surfaceHover;
        else e.currentTarget.style.background = theme.surfaceHover;
      }}
      onMouseLeave={(e) => {
        if (variant === "primary") e.currentTarget.style.background = theme.accent;
        else if (variant === "danger") e.currentTarget.style.background = theme.danger;
        else if (variant === "ghost") e.currentTarget.style.background = "transparent";
        else e.currentTarget.style.background = theme.surface;
      }}
      {...props}
    >
      {children}
    </button>
  );
};

// ─── Input Component ───────────────────────────────────────
const Input = ({ label, error, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: theme.text, display: "block", marginBottom: 6 }}>{label}</label>}
    <input
      style={{
        fontFamily: font, fontSize: 14, padding: "10px 14px", borderRadius: 10, width: "100%",
        border: error ? `1px solid ${theme.danger}` : `1px solid ${theme.border}`,
        background: theme.bg, color: theme.text, outline: "none", boxSizing: "border-box",
      }}
      {...props}
    />
    {error && <div style={{ fontFamily: font, fontSize: 12, color: theme.danger, marginTop: 4 }}>{error}</div>}
  </div>
);

const TextArea = ({ label, error, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: theme.text, display: "block", marginBottom: 6 }}>{label}</label>}
    <textarea
      style={{
        fontFamily: font, fontSize: 14, padding: "10px 14px", borderRadius: 10, width: "100%",
        border: error ? `1px solid ${theme.danger}` : `1px solid ${theme.border}`,
        background: theme.bg, color: theme.text, outline: "none", resize: "vertical", minHeight: 80, boxSizing: "border-box",
      }}
      {...props}
    />
    {error && <div style={{ fontFamily: font, fontSize: 12, color: theme.danger, marginTop: 4 }}>{error}</div>}
  </div>
);

const Select = ({ label, options, error, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: theme.text, display: "block", marginBottom: 6 }}>{label}</label>}
    <select
      style={{
        fontFamily: font, fontSize: 14, padding: "10px 14px", borderRadius: 10,
        border: `1px solid ${theme.border}`, background: theme.bg, color: theme.text,
        outline: "none", cursor: "pointer",
      }}
      {...props}
    >
      {options.map((o) => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
          {typeof o === "string" ? o : o.label}
        </option>
      ))}
    </select>
  </div>
);

const Badge = ({ tag }) => {
  const c = TAG_COLORS[tag] || { bg: "#333", text: "#aaa", border: "#555" };
  return (
    <span style={{
      fontFamily: mono, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 6,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>
      {tag}
    </span>
  );
};

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, borderRadius: 16, border: `1px solid ${theme.border}`,
          padding: 28, width: "100%", maxWidth: 460, maxHeight: "85vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: theme.text, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.textMuted, padding: 4 }}>
            {Icons.x({})}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const EmptyState = ({ icon, title, subtitle }) => (
  <div style={{ textAlign: "center", padding: "48px 20px", color: theme.textDim }}>
    <div style={{ marginBottom: 12, opacity: 0.4 }}>{icon}</div>
    <div style={{ fontFamily: font, fontSize: 15, fontWeight: 600, color: theme.textMuted, marginBottom: 6 }}>{title}</div>
    <div style={{ fontFamily: font, fontSize: 13 }}>{subtitle}</div>
  </div>
);

// ─── Main App ────────────────────────────────────────────
export default function JobsCRM() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  const [state, dispatch] = useReducer(reducer, {
    contacts: [],
    activities: [],
    tasks: [],
  });

  const [view, setView] = useState("dashboard");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("All");
  const [filterCity, setFilterCity] = useState("All");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [summaryTimeFilter, setSummaryTimeFilter] = useState("all");
  const [summaryCityFilter, setSummaryCityFilter] = useState("all"); // NEW: Summary city filter
  const [activityTypeFilter, setActivityTypeFilter] = useState("month"); // NEW: month or ytd for dashboard
  const [calendarData, setCalendarData] = useState(""); // NEW: Calendar import data
  const [parsedCalendarEvents, setParsedCalendarEvents] = useState([]); // NEW: Parsed calendar events

  // ─── Authentication ──────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Sign in error:", error);
      alert("Failed to sign in: " + error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  // ─── Real-time Firebase Sync ─────────────────────────────
  useEffect(() => {
    if (!user) return;

    setSyncing(true);

    const contactsQuery = collection(db, `users/${user.uid}/contacts`);
    const unsubContacts = onSnapshot(contactsQuery, (snapshot) => {
      const contacts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch({ type: "SET_CONTACTS", payload: contacts });
    });

    const activitiesQuery = collection(db, `users/${user.uid}/activities`);
    const unsubActivities = onSnapshot(activitiesQuery, (snapshot) => {
      const activities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch({ type: "SET_ACTIVITIES", payload: activities });
    });

    const tasksQuery = collection(db, `users/${user.uid}/tasks`);
    const unsubTasks = onSnapshot(tasksQuery, (snapshot) => {
      const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch({ type: "SET_TASKS", payload: tasks });
      setSyncing(false);
    });

    return () => {
      unsubContacts();
      unsubActivities();
      unsubTasks();
    };
  }, [user]);

  // ─── Computed data ────────────────────────────────────────
  const filteredContacts = useMemo(() => 
    state.contacts.filter((c) => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.email && c.email.toLowerCase().includes(q)) || (c.company && c.company.toLowerCase().includes(q));
      const matchTag = filterTag === "All" || c.tag === filterTag;
      const matchCity = filterCity === "All" || c.city === filterCity;
      return matchSearch && matchTag && matchCity;
    }),
    [state.contacts, search, filterTag, filterCity]
  );

  const selectedContact = useMemo(() => 
    state.contacts.find((c) => c.id === selectedId),
    [state.contacts, selectedId]
  );

  const contactActivities = useMemo(() => 
    selectedId ? state.activities.filter((a) => a.contactId === selectedId) : state.activities,
    [state.activities, selectedId]
  );

  const resetForm = () => { setForm({}); setErrors({}); };

  const handleAddContact = async () => {
    const newErrors = {};
    if (!form.name?.trim()) newErrors.name = "Name is required";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const contact = { 
      id: uid(), 
      name: form.name, 
      email: form.email || "", 
      phone: form.phone || "", 
      company: form.company || "", 
      city: form.city || "", 
      tag: form.tag || "Lead", 
      created: now() 
    };

    try {
      await saveContact(user.uid, contact);
      const activity = { id: uid(), contactId: null, type: "system", content: `Added new contact: ${form.name}`, timestamp: now() };
      await saveActivity(user.uid, activity);
      setModal(null);
      resetForm();
    } catch (error) {
      console.error("Error adding contact:", error);
      alert("Failed to add contact");
    }
  };

  const handleEditContact = async () => {
    const newErrors = {};
    if (!form.name?.trim()) newErrors.name = "Name is required";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const contact = { 
      ...selectedContact, 
      name: form.name, 
      email: form.email || "", 
      phone: form.phone || "", 
      company: form.company || "", 
      city: form.city || "", 
      tag: form.tag 
    };

    try {
      await saveContact(user.uid, contact);
      setModal(null);
      resetForm();
    } catch (error) {
      console.error("Error editing contact:", error);
      alert("Failed to edit contact");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact and all associated activities?")) return;

    try {
      const contactActivities = state.activities.filter((a) => a.contactId === contactId);
      const contactTasks = state.tasks.filter((t) => t.contactId === contactId);
      
      for (const activity of contactActivities) {
        await deleteActivity(user.uid, activity.id);
      }
      
      for (const task of contactTasks) {
        await deleteTask(user.uid, task.id);
      }
      
      await deleteContact(user.uid, contactId);
      
      if (selectedId === contactId) {
        setSelectedId(null);
        setView("contacts");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert("Failed to delete contact");
    }
  };

  const handleAddNote = async () => {
    const newErrors = {};
    if (!form.contactId) newErrors.contactId = "Please select a contact";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build the activity content
    const activityType = form.noteType || "note";
    let content = "";
    
    if (form.location && form.content) {
      content = `${form.location} - ${form.content}`;
    } else if (form.location) {
      content = form.location;
    } else if (form.content) {
      content = form.content;
    } else {
      // If both are empty, just log the activity type
      const activityLabels = {
        call: "Had a call",
        meeting: "Had a meeting", 
        email: "Sent an email",
        meal: "Had a meal",
        text: "Sent a text",
        note: "Added a note"
      };
      content = activityLabels[activityType] || "Activity logged";
    }

    // Use the selected date or current date
    const timestamp = form.activityDate 
      ? new Date(form.activityDate + 'T12:00:00').toISOString() 
      : now();

    const activity = { 
      id: uid(), 
      contactId: form.contactId, 
      type: activityType, 
      content: content, 
      timestamp: timestamp
    };

    try {
      await saveActivity(user.uid, activity);
      setModal(null);
      resetForm();
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Failed to add activity");
    }
  };

  const handleEditActivity = async () => {
    const newErrors = {};
    if (!form.contactId) newErrors.contactId = "Please select a contact";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build the activity content
    const activityType = form.noteType || "note";
    let content = "";
    
    if (form.location && form.content) {
      content = `${form.location} - ${form.content}`;
    } else if (form.location) {
      content = form.location;
    } else if (form.content) {
      content = form.content;
    } else {
      const activityLabels = {
        call: "Had a call",
        meeting: "Had a meeting", 
        email: "Sent an email",
        meal: "Had a meal",
        text: "Sent a text",
        note: "Added a note"
      };
      content = activityLabels[activityType] || "Activity logged";
    }

    // Use the selected date or current date
    const timestamp = form.activityDate 
      ? new Date(form.activityDate + 'T12:00:00').toISOString() 
      : now();

    const activity = { 
      id: form.activityId,
      contactId: form.contactId, 
      type: activityType, 
      content: content, 
      timestamp: timestamp
    };

    try {
      await saveActivity(user.uid, activity);
      setModal(null);
      resetForm();
    } catch (error) {
      console.error("Error editing activity:", error);
      alert("Failed to edit activity");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await deleteActivity(user.uid, activityId);
    } catch (error) {
      console.error("Error deleting activity:", error);
      alert("Failed to delete activity");
    }
  };

  const openDetail = (contactId) => {
    setSelectedId(contactId);
    setView("detail");
  };

  // NEW: Handler functions for reminder buttons in Summary tab
  const handleRemindInDays = async (contactId, days) => {
    const contact = state.contacts.find(c => c.id === contactId);
    if (!contact) return;

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + days);

    const task = {
      id: uid(),
      contactId: contactId,
      title: `Follow up with ${contact.name}`,
      due: dueDate.toISOString(),
      done: false,
      created: now()
    };

    // NEW: Log activity when reminder is set
    const activity = {
      id: uid(),
      contactId: contactId,
      type: "system",
      content: `Reminder set for ${days} days (${fmt(dueDate.toISOString())})`,
      timestamp: now()
    };

    try {
      await saveTask(user.uid, task);
      await saveActivity(user.uid, activity);
    } catch (error) {
      console.error("Error creating reminder:", error);
      alert("Failed to create reminder");
    }
  };

  const handleRemind1Week = (contactId) => handleRemindInDays(contactId, 7);
  const handleRemind1Month = (contactId) => handleRemindInDays(contactId, 30);

  // NEW: Calendar import functionality
  const handleParseCalendar = () => {
    if (!calendarData.trim()) {
      alert("Please paste calendar data first");
      return;
    }

    try {
      // Simple iCal parser - extracts SUMMARY (name) and DTSTART (date)
      const lines = calendarData.split('\n');
      const events = [];
      let currentEvent = {};

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (line === 'BEGIN:VEVENT') {
          currentEvent = {};
        } else if (line.startsWith('SUMMARY:')) {
          currentEvent.name = line.replace('SUMMARY:', '').trim();
        } else if (line.startsWith('DTSTART')) {
          const dateMatch = line.match(/DTSTART[^:]*:(\d{8})/);
          if (dateMatch) {
            const dateStr = dateMatch[1];
            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            currentEvent.date = `${year}-${month}-${day}`;
          }
        } else if (line.startsWith('ATTENDEE')) {
          const emailMatch = line.match(/mailto:([^\s;]+)/);
          if (emailMatch) {
            if (!currentEvent.attendees) currentEvent.attendees = [];
            currentEvent.attendees.push(emailMatch[1]);
          }
        } else if (line === 'END:VEVENT' && currentEvent.name) {
          // Extract potential contact names from event title
          const potentialContacts = extractContactsFromEventTitle(currentEvent.name);
          const eventData = { ...currentEvent }; // Capture current event data
          potentialContacts.forEach(contactName => {
            events.push({
              id: uid(),
              name: contactName,
              eventTitle: eventData.name,
              date: eventData.date,
              attendees: eventData.attendees || [],
              selected: false
            });
          });
          currentEvent = {};
        }
      }

      setParsedCalendarEvents(events);
      if (events.length > 0) {
        setModal("importCalendar");
      } else {
        alert("No contacts found in calendar data. Make sure to paste valid iCal format.");
      }
    } catch (error) {
      console.error("Error parsing calendar:", error);
      alert("Error parsing calendar data. Please check the format.");
    }
  };

  const extractContactsFromEventTitle = (title) => {
    // Remove common meeting words and extract potential names
    const cleanTitle = title
      .replace(/\b(meeting|call|sync|1:1|one on one|chat|discussion|with|and)\b/gi, ' ')
      .trim();
    
    // Split by common separators and filter out short words
    const words = cleanTitle.split(/[,&\-/]/).map(w => w.trim()).filter(w => w.length > 2);
    
    // Return unique names
    return [...new Set(words)];
  };

  const handleToggleCalendarContact = (eventId) => {
    setParsedCalendarEvents(prev => 
      prev.map(event => 
        event.id === eventId ? { ...event, selected: !event.selected } : event
      )
    );
  };

  const handleImportSelectedContacts = async () => {
    const selectedEvents = parsedCalendarEvents.filter(e => e.selected);
    
    if (selectedEvents.length === 0) {
      alert("Please select at least one contact to import");
      return;
    }

    try {
      for (const event of selectedEvents) {
        // Check if contact already exists
        const existingContact = state.contacts.find(c => 
          c.name.toLowerCase() === event.name.toLowerCase()
        );

        if (existingContact) {
          // Add activity for existing contact
          const activity = {
            id: uid(),
            contactId: existingContact.id,
            type: "meeting",
            content: `${event.eventTitle}`,
            timestamp: event.date ? new Date(event.date + 'T12:00:00').toISOString() : now()
          };
          await saveActivity(user.uid, activity);
        } else {
          // Create new contact
          const contact = {
            id: uid(),
            name: event.name,
            email: event.attendees.length > 0 ? event.attendees[0] : "",
            phone: "",
            company: "",
            city: "",
            tag: "Lead",
            created: now()
          };
          await saveContact(user.uid, contact);

          // Add activity for new contact
          const activity = {
            id: uid(),
            contactId: contact.id,
            type: "meeting",
            content: `${event.eventTitle} (imported from calendar)`,
            timestamp: event.date ? new Date(event.date + 'T12:00:00').toISOString() : now()
          };
          await saveActivity(user.uid, activity);
        }
      }

      setModal(null);
      setCalendarData("");
      setParsedCalendarEvents([]);
      alert(`Successfully imported ${selectedEvents.length} contact(s) from calendar`);
    } catch (error) {
      console.error("Error importing contacts:", error);
      alert("Failed to import contacts");
    }
  };

  // ─── Login Screen ─────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ 
        background: theme.bg, 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        color: theme.text,
        fontFamily: font
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        background: theme.bg, 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        fontFamily: font
      }}>
        <div style={{ 
          textAlign: "center", 
          maxWidth: 400, 
          padding: "40px 32px",
          background: theme.surface,
          borderRadius: 16,
          border: `1px solid ${theme.border}`
        }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: theme.text, marginBottom: 8 }}>
            Job's CRM
          </h1>
          <p style={{ fontSize: 14, color: theme.textMuted, marginBottom: 32 }}>
            Sign in with Google to access your contacts and activities
          </p>
          <Button variant="primary" onClick={handleSignIn} style={{ width: "100%" }}>
            Sign in with Google
          </Button>
        </div>
      </div>
    );
  }

  // ─── Main App UI ──────────────────────────────────────────
  return (
    <div style={{ background: theme.bg, minHeight: "100vh", fontFamily: font, color: theme.text }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${theme.border}`,
        background: theme.surface,
        padding: "14px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 16, color: "#fff"
          }}>
            J
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Job's CRM</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {syncing && (
            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: mono }}>
              Syncing...
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img 
              src={user.photoURL} 
              alt={user.displayName}
              style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${theme.border}` }}
            />
            <span style={{ fontSize: 13, color: theme.textMuted }}>{user.displayName}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            {Icons.signOut({ size: 16 })} Sign Out
          </Button>
        </div>
      </div>

      <div style={{ display: "flex" }}>
        {/* Sidebar */}
        <div style={{
          width: 240, background: theme.surface, borderRight: `1px solid ${theme.border}`,
          minHeight: "calc(100vh - 65px)", padding: "20px 0",
        }}>
          <nav style={{ display: "flex", flexDirection: "column", gap: 3, paddingLeft: 16, paddingRight: 16 }}>
            {[
              { id: "dashboard", label: "Dashboard", icon: Icons.dashboard },
              { id: "summary", label: "Summary", icon: Icons.table },
              { id: "contacts", label: "Contacts", icon: Icons.users },
              { id: "activity", label: "Activity", icon: Icons.activity },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => { setView(item.id); setSelectedId(null); }}
                style={{
                  fontFamily: font, fontSize: 14, fontWeight: 600, padding: "10px 14px", borderRadius: 8,
                  border: "none", background: view === item.id ? theme.surfaceHover : "transparent",
                  color: view === item.id ? theme.accent : theme.textMuted,
                  cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (view !== item.id) e.currentTarget.style.background = theme.surfaceHover;
                }}
                onMouseLeave={(e) => {
                  if (view !== item.id) e.currentTarget.style.background = "transparent";
                }}
              >
                {item.icon({ size: 18 })} {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: 32, maxWidth: 1400, margin: "0 auto", width: "100%" }}>
          {view === "dashboard" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 6 }}>Dashboard</h1>
                <p style={{ fontSize: 13, color: theme.textMuted }}>Analytics and insights</p>
              </div>

              {/* Quick Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                {[
                  { label: "Total Contacts", value: state.contacts.length, icon: Icons.users },
                  { label: "Total Activities", value: state.activities.length, icon: Icons.activity },
                  { label: "This Month", value: state.activities.filter((a) => new Date(a.timestamp).getMonth() === new Date().getMonth()).length, icon: Icons.calendar },
                  { label: "Pending Reminders", value: state.tasks.filter(t => !t.done).length, icon: Icons.bell },
                ].map((card) => (
                  <div key={card.label} style={{
                    background: theme.surface, border: `1px solid ${theme.borderLight}`, borderRadius: 12,
                    padding: "18px 20px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{card.value}</div>
                        <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: mono, marginTop: 4 }}>{card.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts Row 1: Monthly Touchpoints & Activity Types */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
                {/* Monthly Touchpoints Chart */}
                <div style={{
                  background: theme.surface, border: `1px solid ${theme.borderLight}`, borderRadius: 12,
                  padding: "20px 24px",
                }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, marginBottom: 16 }}>Monthly Touchpoints (Unique People)</h3>
                  {(() => {
                    // Get last 6 months of data - count UNIQUE contacts per month
                    const months = [];
                    const now = new Date();
                    for (let i = 5; i >= 0; i--) {
                      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                      const uniqueContacts = new Set(
                        state.activities
                          .filter(a => {
                            const activityDate = new Date(a.timestamp);
                            return activityDate.getMonth() === d.getMonth() && 
                                   activityDate.getFullYear() === d.getFullYear() &&
                                   a.contactId; // Only count activities with a contact
                          })
                          .map(a => a.contactId)
                      );
                      months.push({
                        month: d.toLocaleDateString('en-US', { month: 'short' }),
                        count: uniqueContacts.size
                      });
                    }
                    const maxCount = Math.max(...months.map(m => m.count), 1);
                    
                    return (
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 16, height: 200, paddingTop: 10 }}>
                        {months.map((m, i) => (
                          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                            <div style={{ 
                              width: "100%", 
                              background: theme.accent, 
                              borderRadius: "6px 6px 0 0",
                              height: `${(m.count / maxCount) * 180}px`,
                              minHeight: m.count > 0 ? "20px" : "0px",
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              paddingTop: 8,
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#fff"
                            }}>
                              {m.count > 0 ? m.count : ""}
                            </div>
                            <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: mono }}>{m.month}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Activity Type Pie Chart */}
                <div style={{
                  background: theme.surface, border: `1px solid ${theme.borderLight}`, borderRadius: 12,
                  padding: "20px 24px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Activity Types</h3>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Button
                        size="sm"
                        variant={activityTypeFilter === "month" ? "primary" : "default"}
                        onClick={() => setActivityTypeFilter("month")}
                      >
                        This Month
                      </Button>
                      <Button
                        size="sm"
                        variant={activityTypeFilter === "ytd" ? "primary" : "default"}
                        onClick={() => setActivityTypeFilter("ytd")}
                      >
                        YTD
                      </Button>
                    </div>
                  </div>
                  {(() => {
                    // Filter activities based on selected time period
                    const now = new Date();
                    const filteredActivities = state.activities.filter(a => {
                      const activityDate = new Date(a.timestamp);
                      if (activityTypeFilter === "month") {
                        return activityDate.getMonth() === now.getMonth() && 
                               activityDate.getFullYear() === now.getFullYear();
                      } else { // ytd
                        return activityDate.getFullYear() === now.getFullYear();
                      }
                    });

                    const activityCounts = {
                      meal: filteredActivities.filter(a => a.type === "meal").length,
                      call: filteredActivities.filter(a => a.type === "call").length,
                      text: filteredActivities.filter(a => a.type === "text").length,
                      email: filteredActivities.filter(a => a.type === "email").length,
                      meeting: filteredActivities.filter(a => a.type === "meeting").length,
                      note: filteredActivities.filter(a => a.type === "note").length,
                    };
                    const total = Object.values(activityCounts).reduce((a, b) => a + b, 0);
                    const colors = {
                      meal: "#F59E0B",
                      call: "#3B82F6",
                      text: "#8B5CF6",
                      email: "#10B981",
                      meeting: "#EF4444",
                      note: "#6B7280",
                    };
                    
                    return total === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px 0", color: theme.textDim }}>
                        <div style={{ fontSize: 13 }}>No activities yet</div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {Object.entries(activityCounts)
                          .filter(([_, count]) => count > 0)
                          .sort((a, b) => b[1] - a[1])
                          .map(([type, count]) => (
                            <div key={type} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{
                                width: 12, height: 12, borderRadius: 3,
                                background: colors[type]
                              }} />
                              <div style={{ flex: 1, fontSize: 13 }}>
                                {getActivityIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)}
                              </div>
                              <div style={{ fontSize: 13, fontWeight: 600, fontFamily: mono }}>
                                {count}
                              </div>
                              <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: mono, width: 40, textAlign: "right" }}>
                                {Math.round((count / total) * 100)}%
                              </div>
                            </div>
                          ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Charts Row 2: Top Companies & Upcoming Reminders */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* Top 10 Companies Chart */}
                <div style={{
                  background: theme.surface, border: `1px solid ${theme.borderLight}`, borderRadius: 12,
                  padding: "20px 24px",
                }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, marginBottom: 16 }}>Contacts by Company (Top 10)</h3>
                  {(() => {
                    const companyCounts = {};
                    state.contacts.forEach(c => {
                      const company = c.company || "No Company";
                      companyCounts[company] = (companyCounts[company] || 0) + 1;
                    });
                    
                    const sortedCompanies = Object.entries(companyCounts)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 10);
                    
                    const maxCount = Math.max(...sortedCompanies.map(c => c[1]), 1);
                    
                    return sortedCompanies.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px 0", color: theme.textDim }}>
                        <div style={{ fontSize: 13 }}>No contacts yet</div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {sortedCompanies.map(([company, count]) => (
                          <div key={company} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ 
                              fontSize: 13, 
                              minWidth: 120, 
                              overflow: "hidden", 
                              textOverflow: "ellipsis", 
                              whiteSpace: "nowrap" 
                            }}>
                              {company}
                            </div>
                            <div style={{ flex: 1, height: 24, background: theme.borderLight, borderRadius: 4, overflow: "hidden" }}>
                              <div style={{
                                height: "100%",
                                width: `${(count / maxCount) * 100}%`,
                                background: theme.accent,
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: 8,
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#fff",
                                minWidth: count > 0 ? "24px" : "0"
                              }}>
                                {count}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Upcoming Reminders */}
                <div style={{
                  background: theme.surface, border: `1px solid ${theme.borderLight}`, borderRadius: 12,
                  padding: "20px 24px",
                }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, marginBottom: 16 }}>Upcoming Reminders</h3>
                  {(() => {
                    const upcomingTasks = state.tasks
                      .filter(t => {
                        if (t.done) return false;
                        
                        // Check if there's an activity for this contact after the due date
                        const dueDate = new Date(t.due);
                        const hasActivityAfterDue = state.activities.some(a => 
                          a.contactId === t.contactId && new Date(a.timestamp) > dueDate
                        );
                        
                        // Don't show reminder if activity was logged after due date
                        return !hasActivityAfterDue;
                      })
                      .sort((a, b) => new Date(a.due) - new Date(b.due))
                      .slice(0, 8);
                    
                    return upcomingTasks.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px 0", color: theme.textDim }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                        <div style={{ fontSize: 13 }}>No upcoming reminders</div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflowY: "auto" }}>
                        {upcomingTasks.map((task) => {
                          const contact = state.contacts.find(c => c.id === task.contactId);
                          const dueDate = new Date(task.due);
                          const today = new Date();
                          const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                          const isOverdue = daysLeft < 0;
                          const isDueToday = daysLeft === 0;
                          
                          return (
                            <div key={task.id} style={{
                              padding: "10px 12px",
                              borderRadius: 8,
                              background: theme.bg,
                              border: `1px solid ${isOverdue ? theme.danger : isDueToday ? theme.warning : theme.borderLight}`,
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>
                                  {contact ? contact.name : "No Contact"}
                                </div>
                                <div style={{ 
                                  fontSize: 11, 
                                  color: isOverdue ? theme.danger : isDueToday ? theme.warning : theme.textMuted,
                                  fontFamily: mono,
                                  fontWeight: 600
                                }}>
                                  {isOverdue ? `${Math.abs(daysLeft)}d overdue` : isDueToday ? "Today" : `${daysLeft}d left`}
                                </div>
                              </div>
                              <div style={{ fontSize: 12, color: theme.textMuted }}>
                                {task.title}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {view === "summary" && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, marginBottom: 6 }}>Summary</h1>
                <p style={{ fontSize: 15, color: theme.textMuted, marginBottom: 16 }}>Activity breakdown by person</p>
                
                {/* Time filter buttons */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  {[
                    { id: "all", label: "All Time" },
                    { id: "week", label: "Last Week" },
                    { id: "month", label: "Last Month" },
                    { id: "quarter", label: "Last Quarter" },
                    { id: "ytd", label: "Year to Date" }
                  ].map((filter) => (
                    <Button
                      key={filter.id}
                      size="sm"
                      variant={summaryTimeFilter === filter.id ? "primary" : "default"}
                      onClick={() => setSummaryTimeFilter(filter.id)}
                    >
                      {filter.label}
                    </Button>
                  ))}
                </div>

                {/* NEW: City filter buttons */}
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { id: "all", label: "All Cities" },
                    ...CITIES.map(city => ({ id: city, label: city }))
                  ].map((filter) => (
                    <Button
                      key={filter.id}
                      size="sm"
                      variant={summaryCityFilter === filter.id ? "primary" : "default"}
                      onClick={() => setSummaryCityFilter(filter.id)}
                    >
                      {filter.label}
                    </Button>
                  ))}
                </div>
              </div>

              {(() => {
                // Calculate date ranges for filtering
                const now = new Date();
                const getFilterDate = () => {
                  switch (summaryTimeFilter) {
                    case "week":
                      const weekAgo = new Date(now);
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return weekAgo;
                    case "month":
                      const monthAgo = new Date(now);
                      monthAgo.setMonth(monthAgo.getMonth() - 1);
                      return monthAgo;
                    case "quarter":
                      const quarterAgo = new Date(now);
                      quarterAgo.setMonth(quarterAgo.getMonth() - 3);
                      return quarterAgo;
                    case "ytd":
                      return new Date(now.getFullYear(), 0, 1);
                    default:
                      return null;
                  }
                };

                const filterDate = getFilterDate();

                // NEW: Filter contacts by city first
                let filteredContactsByCity = state.contacts;
                if (summaryCityFilter !== "all") {
                  filteredContactsByCity = state.contacts.filter(c => c.city === summaryCityFilter);
                }

                const summaryData = filteredContactsByCity.map((contact) => {
                  // Filter activities by time range
                  let contactActivities = state.activities.filter((a) => a.contactId === contact.id);
                  if (filterDate) {
                    contactActivities = contactActivities.filter((a) => new Date(a.timestamp) >= filterDate);
                  }

                  const meals = contactActivities.filter((a) => a.type === "meal").length;
                  const calls = contactActivities.filter((a) => a.type === "call").length;
                  const texts = contactActivities.filter((a) => a.type === "text").length;
                  const emails = contactActivities.filter((a) => a.type === "email").length;
                  const meetings = contactActivities.filter((a) => a.type === "meeting").length;
                  const total = meals + calls + texts + emails + meetings;
                  
                  const lastActivity = contactActivities.length > 0 
                    ? contactActivities.reduce((latest, a) => new Date(a.timestamp) > new Date(latest.timestamp) ? a : latest)
                    : null;

                  return {
                    id: contact.id,
                    name: contact.name,
                    company: contact.company || "—",
                    tag: contact.tag,
                    meals,
                    calls,
                    texts,
                    emails,
                    meetings,
                    total,
                    lastTouchpoint: lastActivity ? lastActivity.timestamp : null,
                    lastActivityType: lastActivity ? lastActivity.type : null, // NEW: Track last activity type
                  };
                }).filter((row) => row.total > 0);

                // Sort by last touchpoint (most recent first)
                summaryData.sort((a, b) => {
                  if (!a.lastTouchpoint && !b.lastTouchpoint) return 0;
                  if (!a.lastTouchpoint) return 1;
                  if (!b.lastTouchpoint) return -1;
                  return new Date(b.lastTouchpoint) - new Date(a.lastTouchpoint);
                });

                return summaryData.length === 0 ? (
                  <EmptyState 
                    icon={Icons.table({ size: 40 })} 
                    title="No activity to summarize" 
                    subtitle="Start logging meals, calls, and meetings to see your summary" 
                  />
                ) : (
                  <div style={{ 
                    overflowX: "auto", 
                    background: theme.surface, 
                    border: `1px solid ${theme.borderLight}`, 
                    borderRadius: 12 
                  }}>
                    <table style={{ 
                      width: "100%", 
                      borderCollapse: "collapse", 
                      fontFamily: font, 
                      fontSize: 15
                    }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
                          <th style={{ 
                            textAlign: "left", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>NAME</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>🍽️ MEALS</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>📞 CALLS</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>💬 TEXTS</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>✉️ EMAILS</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>🤝 MEETINGS</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>TOTAL</th>
                          <th style={{ 
                            textAlign: "left", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>LAST TOUCHPOINT</th>
                          <th style={{ 
                            textAlign: "center", 
                            padding: "16px 18px", 
                            fontFamily: mono, 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: theme.textMuted 
                          }}>REMINDERS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.map((row) => (
                          <tr 
                            key={row.id} 
                            style={{ 
                              borderBottom: `1px solid ${theme.borderLight}`,
                            }}
                          >
                            <td style={{ padding: "14px 18px" }}>
                              <div 
                                onClick={() => openDetail(row.id)}
                                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.7"}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                              >
                                <div style={{
                                  width: 36, height: 36, borderRadius: 8, 
                                  background: theme.accentLight,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontWeight: 600, fontSize: 13, color: theme.accent,
                                  border: `1px solid ${theme.border}`
                                }}>
                                  {row.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: 15 }}>{row.name}</div>
                                  <div style={{ 
                                    fontSize: 12, 
                                    color: theme.textMuted, 
                                    fontFamily: mono 
                                  }}>
                                    {row.company}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ 
                              textAlign: "center", 
                              padding: "14px 18px", 
                              fontFamily: mono,
                              fontSize: 15,
                              fontWeight: row.meals > 0 ? 600 : 400,
                              color: row.meals > 0 ? theme.text : theme.textDim
                            }}>{row.meals || "—"}</td>
                            <td style={{ 
                              textAlign: "center", 
                              padding: "14px 18px", 
                              fontFamily: mono,
                              fontSize: 15,
                              fontWeight: row.calls > 0 ? 600 : 400,
                              color: row.calls > 0 ? theme.text : theme.textDim
                            }}>{row.calls || "—"}</td>
                            <td style={{ 
                              textAlign: "center", 
                              padding: "14px 18px", 
                              fontFamily: mono,
                              fontSize: 15,
                              fontWeight: row.texts > 0 ? 600 : 400,
                              color: row.texts > 0 ? theme.text : theme.textDim
                            }}>{row.texts || "—"}</td>
                            <td style={{ 
                              textAlign: "center", 
                              padding: "14px 18px", 
                              fontFamily: mono,
                              fontSize: 15,
                              fontWeight: row.emails > 0 ? 600 : 400,
                              color: row.emails > 0 ? theme.text : theme.textDim
                            }}>{row.emails || "—"}</td>
                            <td style={{ 
                              textAlign: "center", 
                              padding: "14px 18px", 
                              fontFamily: mono,
                              fontSize: 15,
                              fontWeight: row.meetings > 0 ? 600 : 400,
                              color: row.meetings > 0 ? theme.text : theme.textDim
                            }}>{row.meetings || "—"}</td>
                            <td style={{ 
                              textAlign: "center", 
                              padding: "14px 18px", 
                              fontFamily: mono, 
                              fontSize: 15,
                              fontWeight: 700,
                              color: theme.accent
                            }}>{row.total}</td>
                            <td style={{ 
                              padding: "14px 18px", 
                              fontFamily: mono, 
                              fontSize: 13,
                              color: theme.textMuted
                            }}>
                              {row.lastTouchpoint ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontSize: 16 }}>{getActivityIcon(row.lastActivityType)}</span>
                                  <span>{fmt(row.lastTouchpoint)}</span>
                                </div>
                              ) : "—"}
                            </td>
                            {/* NEW: Round reminder buttons */}
                            <td style={{ padding: "14px 18px" }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                                <button
                                  onClick={() => handleRemind1Week(row.id)}
                                  style={{
                                    fontFamily: font,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    padding: "6px 14px",
                                    borderRadius: 20,
                                    border: `1.5px solid ${theme.accent}`,
                                    background: theme.surface,
                                    color: theme.accent,
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = theme.accent;
                                    e.currentTarget.style.color = "#fff";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = theme.surface;
                                    e.currentTarget.style.color = theme.accent;
                                  }}
                                >
                                  1 Week
                                </button>
                                <button
                                  onClick={() => handleRemind1Month(row.id)}
                                  style={{
                                    fontFamily: font,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    padding: "6px 14px",
                                    borderRadius: 20,
                                    border: `1.5px solid ${theme.accent}`,
                                    background: theme.surface,
                                    color: theme.accent,
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = theme.accent;
                                    e.currentTarget.style.color = "#fff";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = theme.surface;
                                    e.currentTarget.style.color = theme.accent;
                                  }}
                                >
                                  1 Month
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}

          {view === "contacts" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Contacts</h1>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button onClick={() => setModal("calendarImport")}>
                    {Icons.calendar({ size: 15 })} Import from Calendar
                  </Button>
                  <Button variant="primary" onClick={() => { resetForm(); setForm({ tag: "Lead" }); setModal("addContact"); }}>
                    {Icons.plus({ size: 15, color: "#fff" })} Add Contact
                  </Button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                  <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                    {Icons.search({ size: 15, color: theme.textDim })}
                  </div>
                  <input
                    placeholder="Search contacts..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      fontFamily: font, fontSize: 14, padding: "10px 14px 10px 36px", borderRadius: 10, width: "100%",
                      border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  style={{
                    fontFamily: font, fontSize: 13, padding: "8px 14px", borderRadius: 10,
                    border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, cursor: "pointer", outline: "none",
                  }}
                >
                  <option value="All">All Tags</option>
                  {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  style={{
                    fontFamily: font, fontSize: 13, padding: "8px 14px", borderRadius: 10,
                    border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, cursor: "pointer", outline: "none",
                  }}
                >
                  <option value="All">All Cities</option>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {filteredContacts.length === 0 ? (
                <EmptyState icon={Icons.users({ size: 40 })} title="No contacts found" subtitle="Try adjusting your search or add a new contact" />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {filteredContacts.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => openDetail(c.id)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "14px 18px", borderRadius: 10, cursor: "pointer",
                        background: theme.surface, border: `1px solid ${theme.borderLight}`,
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover}
                      onMouseLeave={(e) => e.currentTarget.style.background = theme.surface}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: theme.accentLight,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 600, fontSize: 14, color: theme.accent,
                          border: `1px solid ${theme.border}`
                        }}>
                          {c.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600 }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: mono }}>
                            {c.email || c.phone || "No contact info"}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {c.company && (
                          <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: mono }}>
                            {Icons.building({ size: 14 })} {c.company}
                          </div>
                        )}
                        <Badge tag={c.tag} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {view === "detail" && selectedContact && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
                <button
                  onClick={() => setView("contacts")}
                  style={{
                    background: "none", border: "none", cursor: "pointer", color: theme.textMuted,
                    padding: 8, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {Icons.chevronLeft({})}
                </button>
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 4 }}>{selectedContact.name}</h1>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Badge tag={selectedContact.tag} />
                    {selectedContact.company && (
                      <span style={{ fontSize: 13, color: theme.textMuted, fontFamily: mono }}>
                        {Icons.building({ size: 14 })} {selectedContact.company}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    onClick={() => {
                      setForm({ ...selectedContact });
                      setModal("editContact");
                    }}
                  >
                    {Icons.edit({})} Edit
                  </Button>
                  <Button variant="danger" onClick={() => handleDeleteContact(selectedContact.id)}>
                    {Icons.trash({})} Delete
                  </Button>
                </div>
              </div>

              <div style={{
                background: theme.surface, border: `1px solid ${theme.borderLight}`, borderRadius: 12,
                padding: 20, marginBottom: 24,
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
                  {selectedContact.email && (
                    <div>
                      <div style={{ fontSize: 11, fontFamily: mono, color: theme.textMuted, marginBottom: 4 }}>EMAIL</div>
                      <div style={{ fontSize: 14, color: theme.text }}>{selectedContact.email}</div>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div>
                      <div style={{ fontSize: 11, fontFamily: mono, color: theme.textMuted, marginBottom: 4 }}>PHONE</div>
                      <div style={{ fontSize: 14, color: theme.text }}>{selectedContact.phone}</div>
                    </div>
                  )}
                  {selectedContact.company && (
                    <div>
                      <div style={{ fontSize: 11, fontFamily: mono, color: theme.textMuted, marginBottom: 4 }}>COMPANY</div>
                      <div style={{ fontSize: 14, color: theme.text }}>{selectedContact.company}</div>
                    </div>
                  )}
                  {selectedContact.city && (
                    <div>
                      <div style={{ fontSize: 11, fontFamily: mono, color: theme.textMuted, marginBottom: 4 }}>CITY</div>
                      <div style={{ fontSize: 14, color: theme.text }}>{selectedContact.city}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 11, fontFamily: mono, color: theme.textMuted, marginBottom: 4 }}>ADDED</div>
                    <div style={{ fontSize: 14, color: theme.text }}>{fmt(selectedContact.created)}</div>
                  </div>
                </div>
              </div>

              {/* Two column layout: All Activities (left) and Known Associates (right) */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
                {/* All Activities Section */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>All Activities</h2>
                    <Button variant="primary" onClick={() => { resetForm(); setForm({ contactId: selectedId, noteType: "note" }); setModal("addNote"); }}>
                      {Icons.plus({ size: 15, color: "#fff" })} Log Activity
                    </Button>
                  </div>

                  {contactActivities.length === 0 ? (
                    <EmptyState icon={Icons.activity({ size: 32 })} title="No activities" subtitle="Log meals, calls, notes, and more for this contact" />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {contactActivities.map((activity) => (
                        <div key={activity.id} style={{
                          display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 18px", borderRadius: 10,
                          background: theme.surface, border: `1px solid ${theme.borderLight}`,
                        }}>
                          <div style={{ fontSize: 18, marginTop: 2 }}>
                            {getActivityIcon(activity.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                              <span style={{
                                fontFamily: mono,
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                color: theme.accent,
                              }}>
                                {activity.type}
                              </span>
                            </div>
                            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                              {activity.content}
                            </div>
                            <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: mono, marginTop: 6 }}>
                              {fmt(activity.timestamp)} · {fmtTime(activity.timestamp)}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={() => {
                                setForm({
                                  activityId: activity.id,
                                  contactId: activity.contactId,
                                  noteType: activity.type,
                                  content: activity.content,
                                  location: activity.content.includes(' - ') ? activity.content.split(' - ')[0] : '',
                                  activityDate: activity.timestamp.split('T')[0],
                                });
                                setModal("editActivity");
                              }}
                              style={{
                                background: "none", border: "none", cursor: "pointer", color: theme.textMuted,
                                padding: 6, borderRadius: 6,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = theme.accent}
                              onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
                            >
                              {Icons.edit({ size: 14 })}
                            </button>
                            <button
                              onClick={() => handleDeleteActivity(activity.id)}
                              style={{
                                background: "none", border: "none", cursor: "pointer", color: theme.textDim,
                                padding: 6, borderRadius: 6,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = theme.danger}
                              onMouseLeave={(e) => e.currentTarget.style.color = theme.textDim}
                            >
                              {Icons.trash({ size: 14 })}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Known Associates Section */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Known Associates</h2>
                  </div>

                  {(() => {
                    // Find people from the same company
                    const associates = selectedContact.company 
                      ? state.contacts.filter(c => 
                          c.id !== selectedContact.id && 
                          c.company === selectedContact.company &&
                          c.company.trim() !== ""
                        )
                      : [];

                    return associates.length === 0 ? (
                      <div style={{
                        background: theme.surface, 
                        border: `1px solid ${theme.borderLight}`, 
                        borderRadius: 10,
                        padding: "40px 20px",
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🤝</div>
                        <div style={{ fontSize: 13, color: theme.textDim }}>
                          {selectedContact.company ? "No other contacts from this company" : "Add a company to see associates"}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {associates.map((associate) => (
                          <div
                            key={associate.id}
                            onClick={() => openDetail(associate.id)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 14px",
                              borderRadius: 10,
                              background: theme.surface,
                              border: `1px solid ${theme.borderLight}`,
                              cursor: "pointer",
                              transition: "all 0.15s"
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover}
                            onMouseLeave={(e) => e.currentTarget.style.background = theme.surface}
                          >
                            <div style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: theme.accentLight,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 600,
                              fontSize: 13,
                              color: theme.accent,
                              border: `1px solid ${theme.border}`
                            }}>
                              {associate.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 14, fontWeight: 600 }}>{associate.name}</div>
                              <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: mono }}>
                                {associate.email || associate.phone || "No contact info"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {view === "activity" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Activity Log</h1>
                <Button variant="primary" onClick={() => { resetForm(); setModal("addNote"); }}>
                  {Icons.plus({ size: 15, color: "#fff" })} Log Activity
                </Button>
              </div>

              {state.activities.length === 0 ? (
                <EmptyState icon={Icons.activity({ size: 40 })} title="No activity yet" subtitle="Start logging meals, calls, and meetings" />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {state.activities
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((a) => {
                    const contact = state.contacts.find((c) => c.id === a.contactId);
                    return (
                      <div key={a.id} style={{
                        padding: "14px 18px", borderRadius: 10, background: theme.surface, border: `1px solid ${theme.borderLight}`,
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 16 }}>{getActivityIcon(a.type)}</span>
                            <span style={{
                              fontFamily: mono, fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                              color: theme.accent,
                            }}>
                              {a.type}
                            </span>
                            {contact && (
                              <span
                                onClick={() => openDetail(contact.id)}
                                style={{
                                  fontSize: 12, fontWeight: 600, color: theme.accent, cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                {contact.name}
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontSize: 12, color: theme.textMuted, fontFamily: mono }}>
                              {fmt(a.timestamp)} · {fmtTime(a.timestamp)}
                            </span>
                            <button
                              onClick={() => {
                                setForm({
                                  activityId: a.id,
                                  contactId: a.contactId,
                                  noteType: a.type,
                                  content: a.content,
                                  location: a.content.includes(' - ') ? a.content.split(' - ')[0] : '',
                                  activityDate: a.timestamp.split('T')[0],
                                });
                                setModal("editActivity");
                              }}
                              style={{
                                background: "none", border: "none", cursor: "pointer", color: theme.textMuted,
                                padding: 6, borderRadius: 6,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = theme.accent}
                              onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
                            >
                              {Icons.edit({ size: 14 })}
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm("Delete this activity?")) {
                                  handleDeleteActivity(a.id);
                                }
                              }}
                              style={{
                                background: "none", border: "none", cursor: "pointer", color: theme.textDim,
                                padding: 6, borderRadius: 6,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = theme.danger}
                              onMouseLeave={(e) => e.currentTarget.style.color = theme.textDim}
                            >
                              {Icons.trash({ size: 14 })}
                            </button>
                          </div>
                        </div>
                        <div style={{ fontSize: 14 }}>{a.content}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <Modal open={modal === "addContact"} onClose={() => setModal(null)} title="Add Contact">
        <Input
          label="Name"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={errors.name}
          placeholder="John Doe"
        />
        <Input
          label="Email"
          type="email"
          value={form.email || ""}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="john@example.com"
        />
        <Input
          label="Phone"
          type="tel"
          value={form.phone || ""}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+1 (555) 123-4567"
        />
        <Input
          label="Company"
          value={form.company || ""}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
          placeholder="Acme Corp"
        />
        <Select
          label="City"
          options={[
            { value: "", label: "Select a city..." },
            ...CITIES.map(city => ({ value: city, label: city }))
          ]}
          value={form.city || ""}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />
        <Select
          label="Tag"
          options={TAGS}
          value={form.tag || "Lead"}
          onChange={(e) => setForm({ ...form, tag: e.target.value })}
        />
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Button onClick={() => setModal(null)} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleAddContact} style={{ flex: 1 }}>Add Contact</Button>
        </div>
      </Modal>

      <Modal open={modal === "editContact"} onClose={() => setModal(null)} title="Edit Contact">
        <Input
          label="Name"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={errors.name}
          placeholder="John Doe"
        />
        <Input
          label="Email"
          type="email"
          value={form.email || ""}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="john@example.com"
        />
        <Input
          label="Phone"
          type="tel"
          value={form.phone || ""}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+1 (555) 123-4567"
        />
        <Input
          label="Company"
          value={form.company || ""}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
          placeholder="Acme Corp"
        />
        <Select
          label="City"
          options={[
            { value: "", label: "Select a city..." },
            ...CITIES.map(city => ({ value: city, label: city }))
          ]}
          value={form.city || ""}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />
        <Select
          label="Tag"
          options={TAGS}
          value={form.tag || "Lead"}
          onChange={(e) => setForm({ ...form, tag: e.target.value })}
        />
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Button onClick={() => setModal(null)} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleEditContact} style={{ flex: 1 }}>Save Changes</Button>
        </div>
      </Modal>

      <Modal open={modal === "addNote"} onClose={() => setModal(null)} title="Log Activity">
        <Select
          label="Contact"
          options={[
            { value: "", label: "Select a contact..." },
            ...state.contacts.map((c) => ({ value: c.id, label: c.name })),
          ]}
          value={form.contactId || ""}
          onChange={(e) => setForm({ ...form, contactId: e.target.value })}
          error={errors.contactId}
        />
        <Select
          label="Activity Type"
          options={[
            { value: "meal", label: "🍽️ Meal" },
            { value: "call", label: "📞 Call" },
            { value: "text", label: "💬 Text" },
            { value: "email", label: "✉️ Email" },
            { value: "meeting", label: "🤝 Meeting" },
            { value: "note", label: "📝 Note" },
          ]}
          value={form.noteType || "meal"}
          onChange={(e) => setForm({ ...form, noteType: e.target.value })}
        />
        <Input
          label="Location / Subject (Optional)"
          value={form.location || ""}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          placeholder="e.g., Starbucks, Project Discussion"
        />
        <TextArea
          label="Notes (Optional)"
          value={form.content || ""}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Add any additional details..."
        />
        <Input
          label="Date (Optional - defaults to today)"
          type="date"
          value={form.activityDate || ""}
          onChange={(e) => setForm({ ...form, activityDate: e.target.value })}
        />
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Button onClick={() => setModal(null)} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleAddNote} style={{ flex: 1 }}>Log Activity</Button>
        </div>
      </Modal>

      {/* NEW: Edit Activity Modal */}
      <Modal open={modal === "editActivity"} onClose={() => setModal(null)} title="Edit Activity">
        <Select
          label="Contact"
          options={[
            { value: "", label: "Select a contact..." },
            ...state.contacts.map((c) => ({ value: c.id, label: c.name })),
          ]}
          value={form.contactId || ""}
          onChange={(e) => setForm({ ...form, contactId: e.target.value })}
          error={errors.contactId}
        />
        <Select
          label="Activity Type"
          options={[
            { value: "meal", label: "🍽️ Meal" },
            { value: "call", label: "📞 Call" },
            { value: "text", label: "💬 Text" },
            { value: "email", label: "✉️ Email" },
            { value: "meeting", label: "🤝 Meeting" },
            { value: "note", label: "📝 Note" },
          ]}
          value={form.noteType || "meal"}
          onChange={(e) => setForm({ ...form, noteType: e.target.value })}
        />
        <Input
          label="Location / Subject (Optional)"
          value={form.location || ""}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          placeholder="e.g., Starbucks, Project Discussion"
        />
        <TextArea
          label="Notes (Optional)"
          value={form.content || ""}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Add any additional details..."
        />
        <Input
          label="Date"
          type="date"
          value={form.activityDate || ""}
          onChange={(e) => setForm({ ...form, activityDate: e.target.value })}
        />
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Button onClick={() => setModal(null)} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleEditActivity} style={{ flex: 1 }}>Save Changes</Button>
        </div>
      </Modal>

      {/* NEW: Calendar Import Modal - Step 1: Paste Calendar Data */}
      <Modal open={modal === "calendarImport"} onClose={() => { setModal(null); setCalendarData(""); }} title="Import from Calendar">
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: theme.text, display: "block", marginBottom: 6 }}>
            Paste your calendar data (iCal format)
          </label>
          <textarea
            value={calendarData}
            onChange={(e) => setCalendarData(e.target.value)}
            placeholder="Paste your .ics calendar data here..."
            style={{
              fontFamily: mono,
              fontSize: 12,
              padding: "12px",
              borderRadius: 10,
              width: "100%",
              minHeight: 200,
              border: `1px solid ${theme.border}`,
              background: theme.bg,
              color: theme.text,
              outline: "none",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8 }}>
            Export your calendar as .ics file, open it in a text editor, and paste the contents here.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={() => { setModal(null); setCalendarData(""); }} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" onClick={handleParseCalendar} style={{ flex: 1 }}>Parse Calendar</Button>
        </div>
      </Modal>

      {/* NEW: Calendar Import Modal - Step 2: Select Contacts */}
      <Modal 
        open={modal === "importCalendar"} 
        onClose={() => { setModal(null); setParsedCalendarEvents([]); setCalendarData(""); }} 
        title="Select Contacts to Import"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 12 }}>
            Found {parsedCalendarEvents.length} potential contact(s). Select which ones to import:
          </div>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {parsedCalendarEvents.map((event) => (
              <div
                key={event.id}
                onClick={() => handleToggleCalendarContact(event.id)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "12px",
                  borderRadius: 8,
                  background: event.selected ? theme.surfaceHover : theme.bg,
                  border: `1px solid ${event.selected ? theme.accent : theme.border}`,
                  marginBottom: 8,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <input
                  type="checkbox"
                  checked={event.selected}
                  onChange={() => {}}
                  style={{ marginTop: 2, cursor: "pointer" }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                    {event.name}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 2 }}>
                    Event: {event.eventTitle}
                  </div>
                  {event.date && (
                    <div style={{ fontSize: 11, color: theme.textDim, fontFamily: mono }}>
                      Date: {event.date}
                    </div>
                  )}
                  {event.attendees.length > 0 && (
                    <div style={{ fontSize: 11, color: theme.textDim, marginTop: 2 }}>
                      Email: {event.attendees[0]}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={() => { setModal(null); setParsedCalendarEvents([]); setCalendarData(""); }} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleImportSelectedContacts} style={{ flex: 1 }}>
            Import Selected
          </Button>
        </div>
      </Modal>
    </div>
  );
}